import { getLeafValue, getVariationGetter, hasTotalPercentage } from "../../../helper";
import { VisitNodePassage } from "../../../types";
import { generateTooltipItems } from "../tooltips/highchartTooltip";
import { numberFormatters } from "common";
import { NumberFormatKind } from "common/types";
import { DisplayValueType } from "store/current-dashboard/types";
import { getPercentOfTotalDatapointName } from "store/widget-workshop/reducers/addColumnReducer/bar-helper";
const adjustMinMax = (data, minMax) => {
    for (const measure of data) {
        if (measure.data) {
            adjustMinMax(measure.data, minMax);
        }
        else if (hasTotalPercentage(measure.measures)) {
            const percente = measure.measures?.TotalPercentage ?? 0;
            minMax.min = Math.min(percente, minMax.min);
            minMax.max = Math.max(percente, minMax.max);
            minMax.shouldAdjust = true;
        }
    }
};
const getCustomResult = (param, metadata, names, displayType, sumLabel = "") => {
    if (metadata.subSelector && getLeafValue(param.measures?.PARENT) !== metadata.subSelector) {
        return { type: "" };
    }
    let y = getLeafValue(param.measures?.[names.name], getVariationGetter(metadata.displayType)) || 0;
    let formattedName = param.measures?.[names.shortName || ""];
    if (hasTotalPercentage(param.measures) && metadata.displayType === DisplayValueType.Percent) {
        y = param.measures?.TotalPercentage;
        formattedName = numberFormatters(y, NumberFormatKind.percent, { maximumFractionDigits: 2 });
    }
    else if (displayType === DisplayValueType.Percent && metadata.displayType === DisplayValueType.Percent) {
        const totalPercentName = getPercentOfTotalDatapointName(names.name);
        const yInPercentage = param.measures?.[totalPercentName] || y;
        y = yInPercentage;
        formattedName = numberFormatters(yInPercentage, NumberFormatKind.percent, { maximumFractionDigits: 2 });
    }
    const customResult = {
        className: "",
        custom: {
            compositeMeasure: param,
            formatted: formattedName,
            id: param.measures?.Id ? param.measures?.Id?.toString() : param.dimension?.toString(),
            metadata,
            tooltip: generateTooltipItems(param, { ...metadata, formattedFieldName: names.fullName, totalPercentName: names.percentName })
        },
        isSum: !param.dimension?.toString(),
        name: param.dimension?.toString() || sumLabel,
        type: "",
        value: y,
        y
    };
    return customResult;
};
const getParentFilter = (measureName, choice, visitable) => {
    if ((!choice || choice === "") && visitable) {
        let defaultFilter = choice, max = Number.MIN_VALUE * -1;
        for (const option of visitable.data || []) {
            const value = getLeafValue(option.measures?.[measureName]);
            if (value && value > max) {
                max = value;
                defaultFilter = option.dimension?.toString();
            }
        }
        return defaultFilter;
    }
    return choice;
};
const getFormat = (data) => {
    for (const measure of data) {
        if (measure.additionalData?.format) {
            return measure.additionalData.format;
        }
        if (measure.data) {
            return getFormat(measure.data);
        }
    }
    return undefined;
};
const init = (context, measureName, visitable, defaultLabel, dimensionLabel) => {
    const metadata = {
        displayType: context.widgetState?.displayValueType ?? DisplayValueType.Numeral,
        tooltipLabels: Object.entries(context.widgetState?.selectedValues ?? {})
            .map(([key, value]) => {
            const select = context.componentProps?.dataSelectProps[parseInt(key, 10)].selectValues[value];
            return { format: select?.valueFormat, label: select?.displayName };
        })
    };
    if (metadata.tooltipLabels.length === 0) {
        const dataPointMeasure = context.componentProps?.datapointParams?.find((x) => x.measures)?.measures?.[0];
        if (dataPointMeasure) {
            metadata.tooltipLabels.push({ format: context.componentProps?.valueFormat, label: defaultLabel ?? dataPointMeasure?.name });
        }
        if (dimensionLabel) {
            metadata.tooltipLabels.push({ label: dimensionLabel });
        }
    }
    if (context.componentProps?.parentId) {
        metadata.subSelector = getParentFilter(measureName, context.widgetState?.masterDetailsChoice, visitable);
    }
    context.staticData.customResult = [{ metaData: metadata, series: [] }];
};
const addPoint = (context, element, names, sumLabel) => {
    const [container] = context.staticData.customResult || [];
    if (container) {
        const displayType = context.widgetState?.displayValueType ?? DisplayValueType.Numeral;
        const customResult = getCustomResult(element, container.metaData, names, displayType, sumLabel);
        if (customResult.name && customResult.y && !container.series.find((o) => o.name === customResult.name)) {
            if (customResult.isSum) {
                container.series.push(customResult);
            }
            else {
                container.series.unshift(customResult);
            }
        }
    }
};
const finalize = (context, element) => {
    const [container] = context.staticData.customResult || [];
    if (container && element) {
        const adjustedMinMax = { max: 0, min: 1, shouldAdjust: false };
        if (container.metaData.displayType === DisplayValueType.Percent && element?.data) {
            adjustMinMax(element.data, adjustedMinMax);
        }
        if (adjustedMinMax.shouldAdjust && element?.additionalData) {
            element.additionalData.min = adjustedMinMax.min;
            element.additionalData.max = adjustedMinMax.max;
        }
        if (!element.additionalData?.format && element.data) {
            const format = getFormat(element.data);
            if (format) {
                if (!element.additionalData) {
                    element.additionalData = {};
                }
                element.additionalData.format = format;
            }
        }
    }
};
export const highchartsOneMeasure = (params) => (args) => {
    const element = args.visitable;
    const measureName = params?.[0]?.measureName || "VALUE";
    const shortName = params?.[0]?.inShortFormat || "SVALUE";
    const fullName = params?.[0]?.inFullFormat || "FVALUE";
    const percentName = params?.[0]?.inPercent || "PVALUE";
    if (!args.context.staticData.customResult?.length && args.context.visitNodePassage === VisitNodePassage.NodeFirst) {
        init(args.context, measureName, element, params?.[0]?.defaultLabel, params?.[0]?.dimensionLabel);
    }
    else if (args.context.visitNodePassage === VisitNodePassage.Leaf || (args.context.visitNodePassage === VisitNodePassage.NodeLast && params?.[0]?.sumLabel)) {
        addPoint(args.context, element, { fullName, name: measureName, percentName, shortName }, params?.[0]?.sumLabel);
    }
    else if (args.context.visitNodePassage === VisitNodePassage.NodeLast) {
        finalize(args.context, element);
    }
};
